import React, { useState, useCallback, useMemo } from 'react'
import { Accordion, AccordionSummary, AccordionDetails, Typography, Box, TextField } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { LoraType } from '@shared-types'
import { PyrenzBlueButton } from '@client/theme'

interface LoraSelectorProps {
  loras: LoraType[]
  selectedLoras: LoraType[]
  onChange: (loras: LoraType[]) => void
  maxSelect?: number
}

export const LoraSelector = React.memo(function LoraSelector({
  loras,
  selectedLoras,
  onChange,
  maxSelect = 5
}: LoraSelectorProps) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const pageSize = 10

  const toggleLora = useCallback((lora: LoraType) => {
    const isSelected = selectedLoras.some(l => l.id === lora.id)
    let newSelection = [...selectedLoras]

    if (isSelected) {
      newSelection = newSelection.filter(l => l.id !== lora.id)
    } else {
      if (selectedLoras.length >= maxSelect) return 
      newSelection.push(lora)
    }

    onChange(newSelection)
  }, [selectedLoras, onChange, maxSelect])

  const filteredLoras = useMemo(() => {
    if (!search) return loras
    return loras.filter(l => l.name.toLowerCase().includes(search.toLowerCase()))
  }, [loras, search])

  const totalPages = Math.ceil(filteredLoras.length / pageSize)
  const visibleLoras = filteredLoras.slice(page * pageSize, page * pageSize + pageSize)

  return (
    <Accordion sx={{ mb: 2 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography>Select LORA (max {maxSelect})</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search LORAs…"
          value={search}
          onChange={e => {
            setSearch(e.target.value)
            setPage(0)
          }}
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visibleLoras.map(lora => {
            const isSelected = selectedLoras.some(l => l.id === lora.id)
            return (
              <PyrenzBlueButton
                key={lora.id}
                variant="outlined"
                sx={{
                  justifyContent: 'flex-start',
                  gap: 1,
                  textTransform: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  borderColor: isSelected ? 'green' : undefined,
                  color: isSelected ? 'green' : undefined,
                  '&:hover': {
                    transform: 'scale(1.02)',
                    borderColor: isSelected ? 'green' : undefined,
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={() => toggleLora(lora)}
              >
                {lora.images?.[0] && (
                  <img
                    src={lora.images[0]}
                    alt={lora.name}
                    style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                  />
                )}
                <Typography>{lora.name}</Typography>
              </PyrenzBlueButton>
            )
          })}
        </Box>

        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <PyrenzBlueButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>Prev</PyrenzBlueButton>
            <Typography sx={{ alignSelf: 'center' }}>Page {page + 1} / {totalPages}</Typography>
            <PyrenzBlueButton disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next</PyrenzBlueButton>
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  )
})
